<template>
  <div
    class="min-h-full bg-transparent dark:bg-[#0B1120] pb-28 text-slate-800 dark:text-[#F1F5F9] font-sans antialiased"
  >
    <main
      class="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8"
    >
      <!-- HEADER -->
      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200/80 pb-5"
      >
        <div>
          <h1
            class="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5"
          >
            <LayoutSidebarIcon class="h-6 w-6 text-indigo-600" />
            Sidebar & Helpdesk Widgets Dashboard
          </h1>
          <p class="text-xs sm:text-sm text-slate-500 mt-1">
            Configure Admissions Desk details, upcoming calendar events, and
            institutional affiliations.
          </p>
        </div>
      </div>

      <!-- LOADING SKELETON -->
      <div
        v-if="isLoading"
        class="space-y-6"
      >
        <div
          class="h-48 w-full animate-pulse rounded-2xl bg-slate-200/80"
        ></div>
        <div
          class="h-96 w-full animate-pulse rounded-2xl bg-slate-200/80"
        ></div>
      </div>

      <div
        v-else
        class="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-start"
      >
        <!-- LEFT COLUMN: EDITING FORM PANELS (Full width on mobile, 8 COLS on desktop) -->
        <div class="lg:col-span-8 space-y-6 sm:space-y-8">
          <!-- ====================================================
               1. ADMISSIONS HELPDESK WIDGET FORM
          ==================================================== -->
          <section
            class="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-xs space-y-4"
          >
            <div
              class="flex items-center space-x-2.5 border-b border-slate-100 pb-3"
            >
              <HeadsetIcon class="h-5 w-5 text-indigo-600" />
              <div>
                <h2 class="text-base font-bold text-slate-900">
                  1. Admissions Helpdesk Settings
                </h2>
                <p class="text-xs text-slate-500">
                  Public contact details displayed on the sidebar desk widget
                </p>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div>
                <label
                  class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1"
                >
                  Helpline Primary Phone
                </label>
                <div class="relative">
                  <PhoneIcon
                    class="absolute left-3 top-2.5 h-4 w-4 text-slate-400"
                  />
                  <input
                    v-model="form.primaryPhone"
                    type="text"
                    placeholder="+91 7099054777"
                    class="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-xs font-medium text-slate-800 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label
                  class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1"
                >
                  Office Email
                </label>
                <div class="relative">
                  <MailIcon
                    class="absolute left-3 top-2.5 h-4 w-4 text-slate-400"
                  />
                  <input
                    v-model="form.email"
                    type="email"
                    placeholder="info@school.com"
                    class="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-xs font-medium text-slate-800 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label
                  class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1"
                >
                  Office Desk Hours
                </label>
                <div class="relative">
                  <ClockIcon
                    class="absolute left-3 top-2.5 h-4 w-4 text-slate-400"
                  />
                  <input
                    v-model="form.officeHours"
                    type="text"
                    placeholder="Mon–Sat · 8:00 AM–3:00 PM"
                    class="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-xs font-medium text-slate-800 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label
                  class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1"
                >
                  Online Application Link
                </label>
                <div class="relative">
                  <LinkIcon
                    class="absolute left-3 top-2.5 h-4 w-4 text-slate-400"
                  />
                  <input
                    v-model="form.admissionLink"
                    type="text"
                    placeholder="https://admissions.school.com"
                    class="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-xs font-medium text-slate-800 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </section>

          <!-- ====================================================
               2. UPCOMING CALENDAR / EVENTS WIDGET FORM
          ==================================================== -->
          <section
            class="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-xs space-y-4"
          >
            <div
              class="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-2"
            >
              <div class="flex items-center space-x-2.5">
                <CalendarDaysIcon class="h-5 w-5 text-indigo-600" />
                <div>
                  <h2 class="text-base font-bold text-slate-900">
                    2. Upcoming Events Calendar ({{ eventsList.length }})
                  </h2>
                  <p class="text-xs text-slate-500">
                    Manage date-badged event items shown on the sidebar calendar
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 px-3.5 py-2 text-xs font-bold text-white transition hover:bg-indigo-500 active:scale-95 cursor-pointer shadow-xs"
                @click="addEvent"
              >
                <PlusIcon class="h-3.5 w-3.5" />
                <span>Add Event</span>
              </button>
            </div>

            <div class="space-y-3 pt-1">
              <div
                v-for="(eventItem, index) in eventsList"
                :key="eventItem._id || index"
                class="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 space-y-3 transition hover:border-slate-300"
              >
                <div
                  class="flex items-center justify-between border-b border-slate-200/60 pb-2"
                >
                  <span class="text-xs font-mono font-bold text-slate-400"
                    >Event #{{ index + 1 }}</span
                  >
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      :disabled="index === 0"
                      class="p-1 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 disabled:opacity-20 cursor-pointer active:scale-95"
                      @click="moveEvent(index, -1)"
                    >
                      <ArrowUpIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      :disabled="index === eventsList.length - 1"
                      class="p-1 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 disabled:opacity-20 cursor-pointer active:scale-95"
                      @click="moveEvent(index, 1)"
                    >
                      <ArrowDownIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      class="p-1 rounded-md border border-slate-200 bg-white text-rose-500 hover:bg-rose-50 cursor-pointer active:scale-95"
                      @click="removeEvent(index)"
                    >
                      <Trash2Icon class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-12 gap-3">
                  <!-- Date Badge Inputs -->
                  <div class="sm:col-span-3 grid grid-cols-2 gap-2">
                    <div>
                      <label
                        class="block text-[10px] font-semibold text-slate-500 uppercase"
                        >Day No.</label
                      >
                      <input
                        v-model="eventItem.day"
                        type="text"
                        placeholder="18"
                        class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-mono font-bold text-center text-slate-800 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label
                        class="block text-[10px] font-semibold text-slate-500 uppercase"
                        >Month</label
                      >
                      <input
                        v-model="eventItem.month"
                        type="text"
                        placeholder="MAY"
                        class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-mono font-bold text-center uppercase text-slate-800 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <!-- Title -->
                  <div class="sm:col-span-5">
                    <label
                      class="block text-[10px] font-semibold text-slate-500 uppercase"
                      >Event Title</label
                    >
                    <input
                      v-model="eventItem.title"
                      type="text"
                      placeholder="e.g. Science Exhibition"
                      class="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>

                  <!-- Location & Time -->
                  <div class="sm:col-span-4">
                    <label
                      class="block text-[10px] font-semibold text-slate-500 uppercase"
                      >Location & Time</label
                    >
                    <input
                      v-model="eventItem.locationTime"
                      type="text"
                      placeholder="Auditorium · 10:00 AM"
                      class="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div
                v-if="eventsList.length === 0"
                class="p-6 text-center border-2 border-dashed border-slate-200 rounded-xl"
              >
                <CalendarDaysIcon class="mx-auto h-7 w-7 text-slate-400" />
                <p class="mt-1.5 text-xs font-medium text-slate-500">
                  No events listed for the calendar widget.
                </p>
                <button
                  type="button"
                  class="mt-2 text-xs font-bold text-indigo-600 hover:text-indigo-500 cursor-pointer"
                  @click="addEvent"
                >
                  + Add First Event
                </button>
              </div>
            </div>
          </section>

          <!-- ====================================================
               3. AFFILIATION & STATUS WIDGET FORM
          ==================================================== -->
          <section
            class="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-xs space-y-4"
          >
            <div
              class="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-2"
            >
              <div class="flex items-center space-x-2.5">
                <AwardIcon class="h-5 w-5 text-indigo-600" />
                <div>
                  <h2 class="text-base font-bold text-slate-900">
                    3. Affiliation & Institutional Status
                  </h2>
                  <p class="text-xs text-slate-500">
                    Highlighted checklist badges shown on the sidebar
                    accreditation card
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 px-3.5 py-2 text-xs font-bold text-white transition hover:bg-indigo-500 active:scale-95 cursor-pointer shadow-xs"
                @click="addAffiliationItem"
              >
                <PlusIcon class="h-3.5 w-3.5" />
                <span>Add Status Item</span>
              </button>
            </div>

            <div class="space-y-2.5 pt-1">
              <div
                v-for="(item, aIdx) in affiliationList"
                :key="aIdx"
                class="flex items-center gap-2"
              >
                <span
                  class="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 shrink-0"
                >
                  <CheckCircle2Icon class="h-4 w-4" />
                </span>
                <input
                  v-model="affiliationList[aIdx]"
                  type="text"
                  placeholder="e.g. CBSE Affiliated · Affiliation No. 2130123"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
                <button
                  type="button"
                  class="p-1.5 rounded-lg border border-slate-200 bg-white text-rose-500 hover:bg-rose-50 cursor-pointer shrink-0 active:scale-95 transition"
                  @click="removeAffiliationItem(aIdx)"
                >
                  <Trash2Icon class="h-4 w-4" />
                </button>
              </div>

              <div
                v-if="affiliationList.length === 0"
                class="p-6 text-center border-2 border-dashed border-slate-200 rounded-xl"
              >
                <AwardIcon class="mx-auto h-7 w-7 text-slate-400" />
                <p class="mt-1.5 text-xs font-medium text-slate-500">
                  No affiliation status items configured.
                </p>
                <button
                  type="button"
                  class="mt-2 text-xs font-bold text-indigo-600 hover:text-indigo-500 cursor-pointer"
                  @click="addAffiliationItem"
                >
                  + Add Status Highlight
                </button>
              </div>
            </div>
          </section>
        </div>

        <!-- RIGHT COLUMN: LIVE SIDEBAR WIDGETS PREVIEW (Hidden on mobile < lg) -->
        <div class="hidden lg:block lg:col-span-4 lg:sticky lg:top-6 space-y-3">
          <div class="flex items-center justify-between px-1">
            <div class="flex items-center gap-2">
              <span
                class="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"
              ></span>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-500"
              >
                Live Website Sidebar Preview
              </h3>
            </div>
            <span class="text-[10px] font-mono font-semibold text-slate-400"
              >Realtime</span
            >
          </div>

          <!-- REALTIME SIMULATED SIDEBAR -->
          <div
            class="rounded-2xl border border-slate-200/90 bg-[#F6F2E7] p-4 space-y-4 shadow-xs"
          >
            <!-- WIDGET 1 PREVIEW: ADMISSIONS DESK -->
            <div
              class="bg-white border border-[#D8CDAE] rounded-xl p-4 shadow-xs space-y-3"
            >
              <div
                class="flex items-center justify-between border-b border-[#EFE8D6] pb-2 font-serif font-bold text-[#1B2A4A] text-sm"
              >
                <span>Admissions Desk</span>
                <i class="fas fa-headset text-[#B23A34]"></i>
              </div>
              <ul class="space-y-2 text-xs text-slate-700">
                <li class="flex items-start gap-2.5">
                  <i class="fas fa-phone text-[#1F6F64] mt-0.5"></i>
                  <div>
                    <strong class="block text-[11px] text-slate-900"
                      >Call Helpline:</strong
                    >
                    <span class="font-mono text-[11px]">{{
                      form.primaryPhone || "+91 7099054777"
                    }}</span>
                  </div>
                </li>
                <li class="flex items-start gap-2.5">
                  <i class="fas fa-envelope text-[#1F6F64] mt-0.5"></i>
                  <div>
                    <strong class="block text-[11px] text-slate-900"
                      >Email Office:</strong
                    >
                    <span class="text-[11px] break-all">{{
                      form.email || "info@school.com"
                    }}</span>
                  </div>
                </li>
                <li class="flex items-start gap-2.5">
                  <i class="fas fa-clock text-[#1F6F64] mt-0.5"></i>
                  <div>
                    <strong class="block text-[11px] text-slate-900"
                      >Office Hours:</strong
                    >
                    <span class="text-[11px]">{{
                      form.officeHours || "Mon–Sat · 8:00 AM–3:00 PM"
                    }}</span>
                  </div>
                </li>
              </ul>
              <div class="pt-1">
                <a
                  :href="form.admissionLink || '#'"
                  target="_blank"
                  class="w-full inline-flex items-center justify-center gap-2 bg-[#B23A34] text-white py-2 px-3 rounded-md text-xs font-bold shadow-xs hover:bg-[#8C2C27] transition"
                >
                  <i class="fas fa-paper-plane"></i> Apply Online
                </a>
              </div>
            </div>

            <!-- WIDGET 2 PREVIEW: UPCOMING CALENDAR -->
            <div
              class="bg-white border border-[#D8CDAE] rounded-xl p-4 shadow-xs space-y-3"
            >
              <div
                class="flex items-center justify-between border-b border-[#EFE8D6] pb-2 font-serif font-bold text-[#1B2A4A] text-sm"
              >
                <span>Upcoming Calendar</span>
                <i class="fas fa-calendar-days text-[#B23A34]"></i>
              </div>
              <ul class="space-y-2.5">
                <li
                  v-for="(ev, eIdx) in eventsList"
                  :key="eIdx"
                  class="flex items-center gap-3 text-xs"
                >
                  <div
                    class="bg-[#1B2A4A] text-white font-mono text-center px-2 py-1 rounded-md shrink-0 w-12"
                  >
                    <strong class="block text-sm text-[#C99A3B] leading-none">{{
                      ev.day || "01"
                    }}</strong>
                    <span class="text-[9px] uppercase tracking-wider">{{
                      ev.month || "JAN"
                    }}</span>
                  </div>
                  <div class="min-w-0">
                    <b class="block text-slate-900 leading-tight truncate">{{
                      ev.title || "Event Title"
                    }}</b>
                    <span class="text-[10.5px] text-slate-500 truncate block">{{
                      ev.locationTime || "Location & Time"
                    }}</span>
                  </div>
                </li>

                <li
                  v-if="eventsList.length === 0"
                  class="text-xs text-slate-400 italic text-center py-2"
                >
                  No upcoming calendar events.
                </li>
              </ul>
            </div>

            <!-- WIDGET 3 PREVIEW: AFFILIATION & STATUS -->
            <div
              class="bg-white border border-[#D8CDAE] rounded-xl p-4 shadow-xs space-y-3"
            >
              <div
                class="flex items-center justify-between border-b border-[#EFE8D6] pb-2 font-serif font-bold text-[#1B2A4A] text-sm"
              >
                <span>Affiliation & Status</span>
                <i class="fas fa-award text-[#B23A34]"></i>
              </div>
              <ul class="space-y-2 text-xs text-slate-700">
                <li
                  v-for="(aff, affIdx) in affiliationList"
                  :key="affIdx"
                  class="flex items-start gap-2"
                >
                  <i
                    class="fas fa-circle-check text-[#1F6F64] mt-0.5 shrink-0"
                  ></i>
                  <span class="leading-tight">{{ aff }}</span>
                </li>

                <li
                  v-if="affiliationList.length === 0"
                  class="text-xs text-slate-400 italic text-center py-2"
                >
                  No affiliation status items.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- COMPACT FLOATING ACTION BAR (STICKY TO BOTTOM RIGHT ALWAYS) -->
      <teleport to="body">
        <div
          class="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-8 z-[900] max-w-[calc(100vw-2rem)] sm:max-w-lg pointer-events-auto"
        >
          <div
            class="rounded-2xl bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] p-2.5 sm:p-3 shadow-2xl shadow-slate-900/15 flex items-center justify-between gap-3 sm:gap-4 ring-1 ring-slate-950/5 dark:ring-white/5"
          >
            <!-- Status Indicator (Compact) -->
            <div class="flex items-center gap-2 text-xs font-medium min-w-0">
              <!-- Saving State -->
              <span
                v-if="isSaving"
                class="inline-flex items-center gap-1.5 text-indigo-600 dark:text-blue-400 font-semibold truncate"
              >
                <Loader2Icon
                  class="h-4 w-4 animate-spin shrink-0 text-indigo-600 dark:text-blue-400"
                />
                <span class="truncate">Saving...</span>
              </span>

              <!-- Unsaved Changes Badge -->
              <span
                v-else-if="hasUnsavedChanges"
                class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/50 px-2.5 py-1 border border-amber-200/80 dark:border-amber-800/40 text-amber-700 dark:text-amber-400 font-bold shrink-0"
              >
                <span
                  class="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
                ></span>
                <span>Unsaved</span>
              </span>

              <!-- Saved State -->
              <span
                v-else
                class="inline-flex items-center gap-1.5 text-slate-500 dark:text-[#A6B4C8] truncate"
              >
                <CheckCircle2Icon class="h-4 w-4 text-emerald-500 shrink-0" />
                <span class="hidden sm:inline text-xs truncate"
                  >All changes saved</span
                >
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                class="rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-100 dark:hover:bg-[#1F2E47] transition active:scale-95 cursor-pointer"
                :disabled="isSaving"
                @click="confirmReset"
              >
                Discard
              </button>

              <!-- Primary Save Action Button -->
              <button
                type="button"
                class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-4 sm:px-5 py-2 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                :disabled="isSaving"
                @click="handleSave"
              >
                <Loader2Icon
                  v-if="isSaving"
                  class="h-3.5 w-3.5 animate-spin"
                />
                <SaveIcon
                  v-else
                  class="h-3.5 w-3.5"
                />
                <span class="whitespace-nowrap">{{
                  isSaving ? "Saving" : "Save Widgets"
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </teleport>
    </main>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-24 right-4 sm:right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircle2Icon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-2xl bg-white p-5 sm:p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset all sidebar widget configurations back
          to their saved state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    LayoutSidebar as LayoutSidebarIcon,
    Headset as HeadsetIcon,
    Phone as PhoneIcon,
    Mail as MailIcon,
    Clock as ClockIcon,
    Link as LinkIcon,
    CalendarDays as CalendarDaysIcon,
    Award as AwardIcon,
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle2 as CheckCircle2Icon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    ArrowUp as ArrowUpIcon,
    ArrowDown as ArrowDownIcon,
  } from "lucide-vue-next";

  const store = useStore();

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);

  const form = reactive({
    primaryPhone: "",
    email: "",
    officeHours: "",
    admissionLink: "",
  });

  const eventsList = ref([]);
  const affiliationList = ref([]);
  const initialStateJson = ref("");

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  function getCleanSnapshot() {
    return {
      form: { ...form },
      events: eventsList.value.map((e) => ({ ...e })),
      affiliations: [...affiliationList.value],
    };
  }

  const hasUnsavedChanges = computed(() => {
    return JSON.stringify(getCleanSnapshot()) !== initialStateJson.value;
  });

  function snapshotState() {
    initialStateJson.value = JSON.stringify(getCleanSnapshot());
  }

  function addEvent() {
    eventsList.value.push({
      day: "18",
      month: "MAY",
      title: "",
      locationTime: "Auditorium · 10:00 AM",
    });
  }

  function removeEvent(index) {
    eventsList.value.splice(index, 1);
  }

  function moveEvent(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= eventsList.value.length) return;
    const item = eventsList.value.splice(index, 1)[0];
    eventsList.value.splice(targetIndex, 0, item);
  }

  function addAffiliationItem() {
    affiliationList.value.push("");
  }

  function removeAffiliationItem(index) {
    affiliationList.value.splice(index, 1);
  }

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details = store.state.websiteDetails || {};

      form.primaryPhone = details.primaryPhone || "";
      form.email = details.email || "";
      form.officeHours = details.officeHours || "Mon–Sat · 8:00 AM–3:00 PM";
      form.admissionLink = details.admissionLink || "";

      if (
        Array.isArray(details.upcomingEvents) &&
        details.upcomingEvents.length > 0
      ) {
        eventsList.value = details.upcomingEvents.map((e) => ({
          _id: e._id,
          day: e.day || "18",
          month: e.month || "MAY",
          title: e.title || "",
          locationTime: e.locationTime || e.location || "",
        }));
      } else {
        eventsList.value = [
          {
            day: "18",
            month: "MAY",
            title: "Inter-House Science Exhibition",
            locationTime: "Auditorium · 10:00 AM",
          },
          {
            day: "25",
            month: "MAY",
            title: "Annual Sports Meet 2026",
            locationTime: "Sports Ground · 08:30 AM",
          },
        ];
      }

      if (
        Array.isArray(details.affiliationHighlights) &&
        details.affiliationHighlights.length > 0
      ) {
        affiliationList.value = [...details.affiliationHighlights];
      } else {
        affiliationList.value = [
          details.boardAffiliation || "CBSE Affiliated",
          "Managed under DPS Society, New Delhi",
          "Full Digital & Smart Campus",
        ];
      }

      snapshotState();
    } catch (err) {
      showToast("Failed to load widget details", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const handleSave = async () => {
    isSaving.value = true;
    try {
      const payload = {
        primaryPhone: form.primaryPhone,
        email: form.email,
        officeHours: form.officeHours,
        admissionLink: form.admissionLink,
        upcomingEvents: eventsList.value,
        affiliationHighlights: affiliationList.value.filter(Boolean),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      await loadData();
      showToast("Sidebar widgets updated successfully!", "success");
    } catch (err) {
      showToast(err.message || "Error saving widget details", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Widget panel reset to last saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }
</style>
